import type {
  CombinedStatusResponse,
  ParsedStatusSections,
  StatusFeedEntry,
  StatusLevel,
  StatusReportUpdate,
  StatusReportWithTimeline,
} from './types'

export const mapStatusToLevel = (statusRaw?: string | null): StatusLevel => {
  if (!statusRaw) return 'unknown'

  const status = statusRaw.toLowerCase()

  if (status.includes('operational') || status === 'ok' || status === 'success')
    return 'operational'
  if (status.includes('resolv')) return 'operational'
  if (status.includes('monitor')) return 'operational'
  if (status.includes('investig')) return 'impacted'
  if (status.includes('identif')) return 'degraded'
  if (status.includes('degrad')) return 'degraded'
  if (
    status.includes('impact') ||
    status.includes('outage') ||
    status.includes('down')
  ) {
    return 'impacted'
  }
  if (status.includes('maint')) return 'maintenance'

  return 'unknown'
}

const STATUS_SEVERITY: Record<StatusLevel, number> = {
  impacted: 40,
  degraded: 30,
  maintenance: 25,
  unknown: 15,
  operational: 0,
}

const moreSevere = (a: StatusLevel, b: StatusLevel): StatusLevel =>
  STATUS_SEVERITY[b] > STATUS_SEVERITY[a] ? b : a

/** Maps status-page incident update labels (investigating, identified, ...) for timeline dots. */
const mapIncidentUpdateMeta = (
  raw?: string | null
): { level: StatusLevel; label: string } => {
  if (!raw?.trim()) {
    return { level: 'unknown', label: 'Update' }
  }

  const level = mapStatusToLevel(raw)
  const label = raw
    .trim()
    .split(/[\s_]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')

  return { level, label }
}

export const computeDisplayLevel = (
  reports: StatusReportWithTimeline[],
  maintenanceEntries: StatusFeedEntry[],
  combined: CombinedStatusResponse | null
): StatusLevel => {
  if (reports.length > 0) {
    return reports.reduce(
      (acc, r) => moreSevere(acc, r.currentLevel),
      'operational' as StatusLevel
    )
  }

  const ongoingInFeed = maintenanceEntries.some(
    (m) => m.maintPhase === 'ongoing'
  )
  if (combined?.has_ongoing_maintenance || ongoingInFeed) {
    return 'maintenance'
  }

  return 'operational'
}

export const getStatusLevelStyles = (level: StatusLevel) => {
  switch (level) {
    case 'operational':
      return {
        label: 'All Systems Operational',
        badgeClass:
          'bg-status-good-50 text-status-good-800 border border-status-good-200',
        dotClass: 'bg-status-good-500',
        feedBadgeClass:
          'bg-status-good-50 text-status-good-700 ring-status-good-200/80',
      }
    case 'degraded':
      return {
        label: 'Service Degradation',
        badgeClass:
          'bg-status-warning-50 text-status-warning-800 border border-status-warning-200',
        dotClass: 'bg-status-warning-500',
        feedBadgeClass:
          'bg-status-warning-50 text-status-warning-700 ring-status-warning-200/80',
      }
    case 'impacted':
      return {
        label: 'Systems Impacted',
        badgeClass:
          'bg-status-error-50 text-status-error-800 border border-status-error-200',
        dotClass: 'bg-status-error-500',
        feedBadgeClass:
          'bg-status-error-50 text-status-error-700 ring-status-error-200/80',
      }
    case 'maintenance':
      return {
        label: 'Scheduled Maintenance',
        badgeClass:
          'bg-status-info-50 text-status-info-800 border border-status-info-200',
        dotClass: 'bg-status-info-500',
        feedBadgeClass:
          'bg-status-info-50 text-status-info-700 ring-status-info-200/80',
      }
    default:
      return {
        label: 'Status Unknown',
        badgeClass: 'bg-neutral-10 text-neutral-60 border border-hairline',
        dotClass: 'bg-neutral-40',
        feedBadgeClass: 'bg-neutral-100 text-neutral-50 ring-neutral-200/80',
      }
  }
}

const parseStatusReportWithTimeline = (
  entry: any,
  index: number,
  feedUpdatedAt: string | null
): StatusReportWithTimeline => {
  const rawUpdates = Array.isArray(entry.statusReportUpdates)
    ? entry.statusReportUpdates
    : []

  const updates: StatusReportUpdate[] = rawUpdates.map((u: any, i: number) => {
    const { level, label } = mapIncidentUpdateMeta(u.status)
    const dateStr =
      u.updatedAt || u.date || feedUpdatedAt || new Date().toISOString()
    return {
      id: String(u.id ?? `upd-${entry.id ?? index}-${i}`),
      statusRaw: String(u.status ?? ''),
      level,
      label,
      message: typeof u.message === 'string' ? u.message : '',
      date: dateStr,
    }
  })

  const topStatus = entry.status as string | undefined
  const topMessage =
    (typeof entry.message === 'string' ? entry.message : '') ||
    (typeof entry.description === 'string' ? entry.description : '')
  const topDate =
    entry.updatedAt ||
    entry.from ||
    entry.to ||
    feedUpdatedAt ||
    new Date().toISOString()

  if (updates.length === 0) {
    const meta = mapIncidentUpdateMeta(topStatus)
    updates.push({
      id: `single-${entry.id ?? index}`,
      statusRaw: topStatus || '',
      level: meta.level,
      label: meta.label,
      message: topMessage,
      date: topDate,
    })
  }

  updates.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  const latest = updates[0]

  return {
    id: String(entry.id ?? `report-${index}`),
    title: entry.title || entry.name || 'Status report',
    currentLevel: latest.level,
    sortDate: latest.date,
    updates,
  }
}

export const parseStatusFeedSections = (data: any): ParsedStatusSections => {
  if (!data) {
    return { reports: [], maintenances: [], feedUpdatedAt: null }
  }

  const reports: StatusReportWithTimeline[] = []
  const maintenances: StatusFeedEntry[] = []
  const now = Date.now()
  const feedUa = typeof data.updatedAt === 'string' ? data.updatedAt : null

  const statusReports = Array.isArray(data.statusReports)
    ? data.statusReports
    : []
  const maintList = Array.isArray(data.maintenances) ? data.maintenances : []

  statusReports.forEach((entry: any, index: number) => {
    reports.push(parseStatusReportWithTimeline(entry, index, feedUa))
  })

  reports.sort(
    (a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
  )

  maintList.forEach((entry: any, index: number) => {
    const fromRaw = entry.from || entry.start || entry.startsAt
    const toRaw = entry.to || entry.end || entry.endsAt
    const updatedRaw = entry.updatedAt || data.updatedAt

    const fromTime = fromRaw ? new Date(fromRaw).getTime() : undefined
    const toTime = toRaw ? new Date(toRaw).getTime() : undefined

    const startsLater = fromTime != null && fromTime > now
    const ended = toTime != null && toTime < now
    const maintPhase: NonNullable<StatusFeedEntry['maintPhase']> = ended
      ? 'past'
      : startsLater
        ? 'upcoming'
        : 'ongoing'

    const isInFuture = startsLater
    const isInPast = ended

    const baseMessage = entry.message || entry.description || ''
    const scopedMessage = isInPast
      ? baseMessage || 'Completed maintenance window.'
      : isInFuture
        ? baseMessage || 'Upcoming scheduled maintenance window.'
        : baseMessage || 'Ongoing maintenance window.'

    const dateString =
      updatedRaw || fromRaw || toRaw || new Date().toISOString()

    maintenances.push({
      id: String(entry.id ?? `maint-${index}`),
      title:
        entry.name ||
        entry.title ||
        (isInPast
          ? 'Past maintenance'
          : isInFuture
            ? 'Planned maintenance'
            : 'Maintenance'),
      message: scopedMessage,
      status: 'maintenance',
      date: dateString,
      maintPhase,
      maintFrom: fromRaw ?? undefined,
      maintTo: toRaw ?? undefined,
    })
  })

  maintenances.sort(
    (a: StatusFeedEntry, b: StatusFeedEntry) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  return {
    reports,
    maintenances,
    feedUpdatedAt: feedUa,
  }
}

export const formatMaintWindow = (
  from?: string,
  to?: string
): string | null => {
  if (!from) return null
  const start = new Date(from)
  const timeOpts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  const startStr = start.toLocaleString(undefined, timeOpts)
  if (!to) return startStr

  const end = new Date(to)
  const diffMs = end.getTime() - start.getTime()
  const diffH = Math.round(diffMs / (1000 * 60 * 60))
  const durationStr =
    diffH > 0 ? `${diffH} ${diffH === 1 ? 'hour' : 'hours'}` : ''

  return `Duration: ${durationStr}`
}
